import { useCallback } from 'react'
import { Appointment, TaskListItem, TimeManagementListItem } from '@myapp/contract'
import {
  DefaultDurations,
  calculateBlockRangeFromDurations,
  calculateBlockRangeFromAppointment,
  calculateBreakRange,
  calculateBlockRangeFromTask,
  calculateBlockRangeFromTimeManagement,
} from './blockRanges'

// Structural type — any object with startTimeIso satisfies this (including ResolvedBreak)
interface BreakBlockEntry {
  startTimeIso: string
}

interface TechnicianWithAppointments {
  technicianId: string
  appointments: Appointment[]
}

export function useCollisionDetection(
  technicians?: TechnicianWithAppointments[],
  breakBlocks?: Map<string, BreakBlockEntry>,
  timedTasksByTechnician?: Map<string, TaskListItem[]>,
  timeManagementByTechnician?: Map<string, TimeManagementListItem[]>,
  dayIso?: string,
) {
  const getAppointments = useCallback(
    (technicianId: string) =>
      (technicians?.find((t) => t.technicianId === technicianId)?.appointments ?? []).filter(
        (a) => !a.needsRescheduling,
      ),
    [technicians],
  )

  const hasCollision = useCallback(
    (
      anfahrtStartIso: string,
      durations: DefaultDurations,
      technicianId: string,
      excludeId?: string,
      excludeTaskId?: number,
      excludeBreakForTechnicianId?: string,
    ): boolean => {
      const appointments = getAppointments(technicianId)
      const { start: startMs, end: endMs } = calculateBlockRangeFromDurations(anfahrtStartIso, durations)

      for (const appt of appointments) {
        if (excludeId && appt.id === excludeId) continue
        const { start: apptStart, end: apptEnd } = calculateBlockRangeFromAppointment(appt)
        if (startMs < apptEnd && endMs > apptStart) return true
      }

      if (excludeBreakForTechnicianId !== technicianId) {
        const breakBlock = breakBlocks?.get(technicianId)
        if (breakBlock) {
          const { start: breakStart, end: breakEnd } = calculateBreakRange(breakBlock.startTimeIso)
          if (startMs < breakEnd && endMs > breakStart) return true
        }
      }

      const timedTasks = timedTasksByTechnician?.get(technicianId) ?? []
      for (const task of timedTasks) {
        if (excludeTaskId != null && task.id === excludeTaskId) continue
        if (task.needsRescheduling) continue
        const { start: taskStart, end: taskEnd } = calculateBlockRangeFromTask(task)
        if (startMs < taskEnd && endMs > taskStart) return true
      }

      if (dayIso) {
        const tmEntries = timeManagementByTechnician?.get(technicianId) ?? []
        for (const entry of tmEntries) {
          const { start: tmStart, end: tmEnd } = calculateBlockRangeFromTimeManagement(entry, dayIso)
          if (tmStart < tmEnd && startMs < tmEnd && endMs > tmStart) return true
        }
      }

      return false
    },
    [getAppointments, breakBlocks, timedTasksByTechnician, timeManagementByTechnician, dayIso],
  )

  return { hasCollision, getAppointments }
}
